export const NEGOTIATION_PRICE_MODE = "NEGOTIATION";
export const DEFAULT_WHATSAPP_NUMBER = "966532900988";
export const DEFAULT_RENT_PERIOD = "MONTHLY";
export const RENT_PERIOD_PRIORITY = ["MONTHLY", "WEEKLY", "DAILY", "YEARLY"];

export function getRentPriceValue(property, period) {
  const normalizedPeriod = String(period || "").toUpperCase();
  if (normalizedPeriod === "DAILY") return property?.rentPriceDaily;
  if (normalizedPeriod === "WEEKLY") return property?.rentPriceWeekly;
  if (normalizedPeriod === "MONTHLY") return property?.rentPriceMonthly;
  if (normalizedPeriod === "YEARLY") return property?.rentPriceYearly;
  return null;
}

export function isNegotiationPrice(property) {
  return String(property?.priceDisplayMode || "").toUpperCase() === NEGOTIATION_PRICE_MODE;
}

export function getNegotiationWhatsappUrl(property, phoneNumber = DEFAULT_WHATSAPP_NUMBER) {
  const title = property?.title || "this property";
  const location = [property?.area, property?.city].filter(Boolean).join(", ");
  const message = location
    ? `Hello, I would like to negotiate for ${title} in ${location}.`
    : `Hello, I would like to negotiate for ${title}.`;

  return `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
}

export function getRentPeriod(property) {
  if (String(property?.listingType || "").toUpperCase() !== "RENT") return "";
  const explicitPeriod = String(property?.rentPeriod || "").toUpperCase();
  if (["DAILY", "WEEKLY", "MONTHLY", "YEARLY"].includes(explicitPeriod)) {
    return explicitPeriod;
  }

  const inferredPeriod = RENT_PERIOD_PRIORITY.find((period) => {
    const value = getRentPriceValue(property, period);
    return value !== null && value !== undefined && !Number.isNaN(Number(value));
  });

  return inferredPeriod || DEFAULT_RENT_PERIOD;
}

export function getRentPeriodLabel(property, t) {
  const period = getRentPeriod(property);

  if (!period) return "";

  if (typeof t === "function") {
    if (period === "DAILY") return t("property.perDay");
    if (period === "WEEKLY") return t("property.perWeek");
    if (period === "YEARLY") return t("property.perYear");
    return t("property.perMonth");
  }

  if (period === "DAILY") return "/Day";
  if (period === "WEEKLY") return "/Week";
  if (period === "YEARLY") return "/Year";
  return "/Month";
}

export function getPropertyPriceSuffix(property, t, currency = property?.currency || "SAR") {
  const periodLabel = getRentPeriodLabel(property, t);
  return periodLabel ? `${currency} ${periodLabel}` : currency;
}

export function formatPropertyPrice(property, options = {}) {
  const {
    currency = property?.currency || "SAR",
    negotiationText = "Click for negotiation",
  } = options;

  if (isNegotiationPrice(property)) {
    return {
      text: negotiationText,
      isNegotiation: true,
      href: getNegotiationWhatsappUrl(property),
      suffix: "",
    };
  }

  if (property?.price === null || property?.price === undefined || Number.isNaN(Number(property.price))) {
    const rentPeriod = getRentPeriod(property);
    const rentPrice = getRentPriceValue(property, rentPeriod);

    if (String(property?.listingType || "").toUpperCase() === "RENT" && rentPrice != null && !Number.isNaN(Number(rentPrice))) {
      return {
        text: Number(rentPrice).toLocaleString(),
        isNegotiation: false,
        href: "",
        suffix: currency,
        rentPeriod,
      };
    }

    return {
      text: "",
      isNegotiation: false,
      href: "",
      suffix: "",
    };
  }

  if (String(property?.listingType || "").toUpperCase() === "RENT") {
    const rentPeriod = getRentPeriod(property);
    const rentPrice = getRentPriceValue(property, rentPeriod);
    const resolvedPrice = rentPrice != null && !Number.isNaN(Number(rentPrice)) ? rentPrice : property.price;

    return {
      text: Number(resolvedPrice).toLocaleString(),
      isNegotiation: false,
      href: "",
      suffix: currency,
      rentPeriod,
    };
  }

  return {
    text: Number(property.price).toLocaleString(),
    isNegotiation: false,
    href: "",
    suffix: currency,
    rentPeriod: getRentPeriod(property),
  };
}
